import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import {
  ActivityReference,
  DraftShape,
  MezzanineStorey,
  NonMezzanineStorey,
  Shape,
  Storey,
} from '@shared/models/design/storey.model';
import { v4 } from 'uuid';
import { getNextBuildingName } from './building-name.helpers';
import { Point2D, Polygon2D } from '@shared/models/geometry.model';
import {
  ArrayOrSingle,
  ItemOrItemId,
  SemiPartial,
} from '@shared/models/type-helpers.model';
import { getItemById } from '../array.helpers';
import { getId } from '../object.helpers';
import { guards } from '../typeguards.helpers';
import { sum } from 'lodash';
import { designGuards } from './design-typeguards.helpers';
import {
  getCenter,
  isEqualPoints,
  isPolygon2D,
  movePoints,
} from '@shared/helpers/geometry/geometry.helpers';
import { generateRectangle } from './shape-templates.helpers';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import { createShape } from './factories/shape.factory';
import { createStorey, createStoreys } from './factories/storey.factory';
import {
  createActivityReferences,
  createCalculatedAreaActivityRefs,
  createProgramActivityRefs,
} from './factories/activity-reference.factory';
import { activityRefGuards } from './guards/activity-guards.helpers';

/**
 * Selector for a storey. Can be a storey object, id or index.
 */
export type StoreySelector = ItemOrItemId<SemiPartial<Storey, 'id'>> | number;

/**
 * Get a storey by id. TODO: Rename to getStorey
 * Numeric indexes still read the first building (expression compatibility).
 * Id lookups search every {@link Building} so the storey editor can target
 * a non-first building (DEV-2576).
 * @param root
 * @param selector
 * @returns
 */
export const getStoreyById = (
  root: BuildingDesign | Storey[],
  selector?: StoreySelector,
): Storey | undefined => {
  if (!guards.is.defined(selector)) return undefined;
  if (guards.is.number(selector)) {
    return getStoreys(root)[selector];
  }
  const id = getId(selector);
  const storeys = designGuards.is.buildingDesign(root)
    ? getAllStoreys(root)
    : getStoreys(root);
  return storeys.find((s) => s.id === id);
};

/**
 * Get index of a storey in its building's storey list.
 * When {@link root} is a {@link BuildingDesign}, the index is within the
 * building that owns the storey — not a flat list of every building.
 * @param root
 * @param itemOrId
 * @returns
 */
export const getStoreyIndex = (
  root: BuildingDesign | Storey[],
  itemOrId: ItemOrItemId<SemiPartial<Storey, 'id'>>,
): number => {
  const id = getId(itemOrId);
  const storeys = designGuards.is.buildingDesign(root)
    ? (getBuildingContainingStorey(root, id)?.storeys ?? [])
    : getStoreys(root);
  return storeys.findIndex((s) => s.id === id);
};

/**
 * 1-based storey number in `storeys` (ground = 1). Used next to the delete
 * control in the design panel.
 */
export const getStoreyNumber = (
  storey: Storey,
  storeys: readonly Storey[],
): number => {
  const index = storeys.findIndex((s) => s.id === storey.id);
  return index >= 0 ? index + 1 : 1;
};

/**
 * Design-tab label for a storey: {@link Storey.name} when set, otherwise
 * "Storey N" from its 1-based index in `storeys`.
 */
export const getStoreyDisplayName = (
  storey: Storey,
  storeys: readonly Storey[],
): string => {
  if (storey.name) return storey.name;
  return `Storey ${getStoreyNumber(storey, storeys)}`;
};

/**
 * Get storeys from the design or storey. For a {@link BuildingDesign} this
 * returns the storeys of the first {@link Building} (DEV-2576).
 * Use {@link getAllStoreys} to include storeys from every building.
 * @param root
 * @returns
 */
export const getStoreys = (
  root: BuildingDesign | ArrayOrSingle<Storey> | undefined,
): Storey[] => {
  if (!guards.is.defined(root)) return [];
  if (designGuards.is.storey(root)) {
    return [root];
  }
  if (guards.is.array(root)) {
    return root;
  }
  if (!designGuards.is.buildingDesign(root)) return [];
  return root.buildings[0]?.storeys ?? [];
};

/**
 * Get storeys from every {@link Building} in the design (DEV-2576).
 * @param root - Building design
 * @returns Flat list of storeys across all buildings
 */
export const getAllStoreys = (root: BuildingDesign | undefined): Storey[] => {
  if (!designGuards.is.buildingDesign(root)) return [];
  return root.buildings.flatMap((building) =>
    guards.is.array(building.storeys) ? building.storeys : [],
  );
};

/**
 * Create a {@link Building} wrapping the given storeys (DEV-2576).
 * @param storeys - Storeys of the building
 * @param overrides - Optional id/name overrides
 */
export const createDesignBuilding = (
  storeys: Storey[],
  overrides: Partial<Omit<Building, 'storeys'>> = {},
): Building => ({
  id: overrides.id ?? v4(),
  ...(overrides.name !== undefined ? { name: overrides.name } : {}),
  storeys,
});

/** First {@link Building} of a design, or undefined when empty. */
const getFirstBuilding = (
  design: BuildingDesign | undefined,
): Building | undefined => design?.buildings[0];

/**
 * Resolve a {@link Building} by id.
 * Omitting {@link buildingId} returns the first building. An unknown id
 * returns undefined rather than silently falling back (DEV-2576).
 * @param design - Building design
 * @param buildingId - Target building id, or undefined for the first building
 */
export const getBuildingById = (
  design: BuildingDesign | undefined,
  buildingId: string | undefined,
): Building | undefined => {
  if (!design) return undefined;
  if (!buildingId) return getFirstBuilding(design);
  return getItemById(design.buildings, buildingId, false);
};

/**
 * {@link Building} that owns the given storey id, if any.
 * @param design - Building design
 * @param storeyId - Storey id to locate
 */
export const getBuildingContainingStorey = (
  design: BuildingDesign,
  storeyId: string | undefined,
): Building | undefined => {
  if (!storeyId) return undefined;
  return design.buildings.find((building) =>
    building.storeys.some((storey) => storey.id === storeyId),
  );
};

/**
 * Storeys of the {@link Building} that owns {@link selector}.
 * Numeric indexes still read the first building (expression compatibility).
 * When {@link root} is already a storey list, that list is returned as-is.
 * @param root - Design or storey list
 * @param selector - Storey, id, or numeric index
 * @returns Owning building storeys, or the first building for a numeric index
 */
export const getStoreysOwningSelector = (
  root: BuildingDesign | Storey[],
  selector: StoreySelector,
): Storey[] => {
  if (!designGuards.is.buildingDesign(root)) return getStoreys(root);
  if (guards.is.number(selector)) return getStoreys(root);
  return getBuildingContainingStorey(root, getId(selector))?.storeys ?? [];
};

/**
 * True when no storey has a drawable footprint. Extra shapeless floors still
 * count as empty so flatten/pen/GFA can fill them instead of treating the
 * stack as geometry.
 * @param storeys - Storeys of one building
 */
export const isEmptyStoreysGeometry = (
  storeys: Storey[] | undefined,
): boolean =>
  !guards.is.array(storeys) ||
  !storeys.some(
    (storey) => guards.is.array(storey.shapes) && storey.shapes.length > 0,
  );

/**
 * True while the selected building has no footprint and the user has not
 * started a draft — the “click anywhere on the map” overlay.
 */
export const isClickAnywhereDrawMode = (
  storeys: Storey[] | undefined,
  draftShape: DraftShape | undefined,
): boolean => isEmptyStoreysGeometry(storeys) && draftShape === undefined;

/**
 * Fill missing `shapes` / `activities` arrays on a storey so incomplete JSON
 * cannot throw in geometry checks or calculated-area heals.
 * Returns the same {@link storey} when both fields are already arrays.
 */
export const withCompleteStoreyFields = (storey: Storey): Storey => {
  const hasShapes = guards.is.array(storey.shapes);
  const hasActivities = guards.is.array(storey.activities);
  if (hasShapes && hasActivities) return storey;
  return {
    ...storey,
    shapes: hasShapes ? storey.shapes : [],
    activities: hasActivities ? storey.activities : [],
  };
};

/**
 * Ensure every building has a non-empty storeys array and every storey has
 * `shapes` / `activities` arrays. Missing or empty storeys get a default storey.
 * Returns the same {@link design} when nothing is missing.
 */
export const healStoreyCollections = (
  design: BuildingDesign,
): BuildingDesign => {
  let changed = false;
  const buildings = design.buildings.map((building) => {
    if (!guards.is.array(building.storeys) || building.storeys.length === 0) {
      changed = true;
      return { ...building, storeys: createStoreys() };
    }
    const storeys = building.storeys.map(withCompleteStoreyFields);
    if (storeys.every((storey, index) => storey === building.storeys[index])) {
      return building;
    }
    changed = true;
    return { ...building, storeys };
  });
  return changed ? { ...design, buildings } : design;
};

/**
 * True when the building is missing or has no drawable footprint.
 * @param building - Building to inspect
 * @returns True when there is no drawable footprint
 */
export const isEmptyBuildingGeometry = (
  building: Building | undefined,
): boolean => !building || isEmptyStoreysGeometry(building.storeys);

/**
 * True when the selected storeys and every sibling building have no drawable
 * footprint. Used so location rebase / empty overlay do not treat an empty
 * selected building as a blank project when another building still has geometry.
 * @param selectedStoreys - Storeys of the selected building
 * @param otherBuildings - Non-selected buildings
 */
export const isEntireDesignGeometryEmpty = (
  selectedStoreys: Storey[],
  otherBuildings: Building[],
): boolean =>
  isEmptyStoreysGeometry(selectedStoreys) &&
  otherBuildings.every(isEmptyBuildingGeometry);

/**
 * Storeys of the {@link Building} that owns {@link storeyId}.
 * Empty when the storey is not in the design.
 * @param design - Building design
 * @param storeyId - Storey id to locate
 */
export const getStoreysForStoreyId = (
  design: BuildingDesign,
  storeyId: string,
): Storey[] => getBuildingContainingStorey(design, storeyId)?.storeys ?? [];

/**
 * Get the storeys of a specific {@link Building}, falling back to the first
 * building when the id is missing (DEV-2576).
 * @param design - Building design
 * @param buildingId - Target building id, or undefined for the first building
 */
export const getBuildingStoreys = (
  design: BuildingDesign | undefined,
  buildingId: string | undefined,
): Storey[] => getBuildingById(design, buildingId)?.storeys ?? [];

/**
 * Storeys of a building, falling back to the only building of the design when
 * the id resolves to no building (DEV-2576).
 *
 * A cleared selection deliberately resolves to no building, but a
 * single-building design still has an unambiguous building to describe.
 * @param design - Building design
 * @param buildingId - Building id, a cleared-selection sentinel, or undefined
 */
export const getBuildingStoreysWithSoleFallback = (
  design: BuildingDesign | undefined,
  buildingId: string | undefined,
): Storey[] => {
  const building = getBuildingById(design, buildingId);
  if (building) return building.storeys;
  if (design?.buildings.length !== 1) return [];
  return getFirstBuilding(design)?.storeys ?? [];
};

/**
 * Buildings other than the selected one (DEV-2576).
 * Used to render static footprints and to hit-test building selection.
 * An unknown {@link buildingId} returns every building (none excluded) so
 * sibling geometry still drives emptiness checks and static rendering until
 * the selection is cleared.
 * @param design - Building design
 * @param buildingId - Building id to exclude (the selected/editable building)
 */
export const getOtherBuildings = (
  design: BuildingDesign | undefined,
  buildingId: string | undefined,
): Building[] => {
  if (!design) return [];
  const selected = getBuildingById(design, buildingId);
  if (buildingId && !selected) return design.buildings;
  return design.buildings.filter((building) => building !== selected);
};

/** Gap between sibling footprints and a new empty-building placement. */
const EMPTY_BUILDING_FOOTPRINT_GAP_METERS = 5;
/** Clickable square size for an empty building that has no footprint yet. */
const EMPTY_BUILDING_HIT_TARGET_SIZE_METERS = 10;

/**
 * Flatten drawable vertices from {@link storeys}. Missing `shapes` arrays are
 * treated as empty so incomplete JSON cannot throw.
 */
const getStoreysShapePoints = (storeys: Storey[] | undefined): Point2D[] => {
  if (!guards.is.array(storeys)) return [];
  return storeys.flatMap((storey) =>
    withCompleteStoreyFields(storey).shapes.flatMap((shape) => shape.points),
  );
};

/**
 * Center for a footprint placed beside existing geometry: origin when there
 * are no sibling shapes, otherwise to the right of siblings.
 */
const getPlacementOriginFromSiblingPoints = (
  siblingPoints: Point2D[],
  footprintWidth: number,
): Point2D => {
  if (!siblingPoints.length) return [0, 0];
  const maxX = Math.max(...siblingPoints.map((point) => point[0]));
  const [, centerY] = getCenter(...siblingPoints);
  return [
    maxX + EMPTY_BUILDING_FOOTPRINT_GAP_METERS + footprintWidth / 2,
    centerY,
  ];
};

/**
 * Center for a new footprint on an empty building: origin if there are no
 * sibling shapes, otherwise to the right of siblings so it does not overlap.
 * @param design - Building design
 * @param buildingId - Building being filled; omitted = first building
 * @param footprintWidth - Width of the new footprint in metres
 */
export const getEmptyBuildingPlacementOrigin = (
  design: BuildingDesign,
  buildingId: string | undefined,
  footprintWidth: number,
): Point2D => {
  const siblingPoints = getOtherBuildings(design, buildingId).flatMap(
    (building) => getStoreysShapePoints(building.storeys),
  );
  return getPlacementOriginFromSiblingPoints(siblingPoints, footprintWidth);
};

/**
 * Square polygon used as a map hit target for a building with no footprint.
 * Returns undefined when {@link buildingId} is not an empty other-building.
 * Extra empty slots are offset so they do not stack on the same square.
 * @param selectedStoreys - Storeys of the currently selected building
 * @param otherBuildings - Non-selected buildings rendered as static footprints
 * @param buildingId - Empty building that needs a selectable target
 */
export const getEmptyBuildingHitTargetPolygon = (
  selectedStoreys: Storey[],
  otherBuildings: Building[],
  buildingId: string,
): Polygon2D | undefined => {
  const emptySlotIndex = otherBuildings
    .filter(isEmptyBuildingGeometry)
    .findIndex((building) => building.id === buildingId);
  if (emptySlotIndex < 0) return undefined;

  const siblingPoints = [
    ...getStoreysShapePoints(selectedStoreys),
    ...otherBuildings
      .filter(
        (building) =>
          building.id !== buildingId && !isEmptyBuildingGeometry(building),
      )
      .flatMap((building) => getStoreysShapePoints(building.storeys)),
  ];
  const size = EMPTY_BUILDING_HIT_TARGET_SIZE_METERS;
  const origin = getPlacementOriginFromSiblingPoints(siblingPoints, size);
  const slotOffset =
    emptySlotIndex * (size + EMPTY_BUILDING_FOOTPRINT_GAP_METERS);
  const moved = movePoints(generateRectangle(size, size), [
    origin[0] + slotOffset,
    origin[1],
  ]);
  return isPolygon2D(moved) ? moved : undefined;
};

/**
 * Replace the storeys of a specific {@link Building}, falling back to the first
 * building when the id is missing (DEV-2576). An empty buildings list gets a
 * new building with {@link storeys}. Throws when {@link buildingId} is set but
 * no building matches.
 * @param design - Building design
 * @param buildingId - Target building id, or undefined for the first building
 * @param storeys - Next storeys for that building
 * @throws When {@link buildingId} does not match any building
 */
export const setBuildingStoreys = (
  design: BuildingDesign,
  buildingId: string | undefined,
  storeys: Storey[],
): BuildingDesign => {
  if (design.buildings.length === 0) {
    return {
      ...design,
      buildings: [{ id: v4(), storeys }],
    };
  }
  const target = getBuildingById(design, buildingId);
  if (!target) {
    throw new Error(
      `Cannot set storeys: building ${buildingId ?? '(first)'} was not found`,
    );
  }
  return {
    ...design,
    buildings: design.buildings.map((building) =>
      building === target ? { ...building, storeys } : building,
    ),
  };
};

/**
 * Append a {@link Building} to a design (DEV-2576).
 * @param design - Building design
 * @param building - Building to add
 */
export const addBuildingToDesign = (
  design: BuildingDesign,
  building: Building,
): BuildingDesign => ({
  ...design,
  buildings: [...design.buildings, building],
});

export type AppliedBuildingFootprint = {
  design: BuildingDesign;
  building: Building;
};

/**
 * Append a {@link Building} with one shapeless storey so the user can draw
 * like an empty map without entering pen mode (DEV-2576).
 * Copies activity refs from {@link sourceBuildingId}'s ground storey. Preset
 * programs on {@link BuildingDesign.activities} are added when no storey
 * references a program yet, or when the design has exactly one program
 * (DEV-3749, DEV-3765).
 */
export const addEmptyBuildingToDesign = (
  design: BuildingDesign,
  sourceBuildingId?: string,
): AppliedBuildingFootprint => {
  const sourceStorey =
    getBuildingById(design, sourceBuildingId)?.storeys[0] ??
    getSiblingActivitySourceStorey(design);
  const baseActivities = storeyHasActivityRefs(sourceStorey)
    ? createActivityReferences(sourceStorey?.activities)
    : createCalculatedAreaActivityRefs(design.activities);
  const activities = storeyHasProgramActivityRefs(sourceStorey)
    ? baseActivities
    : appendDesignProgramRefs(baseActivities, design);
  const building = createDesignBuilding(
    [
      createStorey({
        shapes: [],
        activities,
      }),
    ],
    { name: getNextBuildingName(design.buildings) },
  );
  return {
    design: addBuildingToDesign(design, building),
    building,
  };
};

/**
 * True when the storey has a non-empty activities array. Missing `activities`
 * (incomplete JSON) is treated as empty so fill helpers do not throw.
 */
const storeyHasActivityRefs = (storey: Storey | undefined): boolean =>
  guards.is.defined(storey) &&
  guards.is.array(storey.activities) &&
  storey.activities.length > 0;

/**
 * True when the storey already has a program (residential, office, …).
 * Calculated-area refs alone do not count.
 */
const storeyHasProgramActivityRefs = (storey: Storey | undefined): boolean =>
  guards.is.defined(storey) &&
  guards.is.array(storey.activities) &&
  storey.activities.some(activityRefGuards.is.programActivityRef);

/**
 * True when any storey already references a program. Several preset programs
 * are seeded only while this is false, so a second building is not given an
 * even split of every program on the design (DEV-3749). A single program is
 * still applied (DEV-3765).
 */
const designHasProgramActivityRefs = (design: BuildingDesign): boolean =>
  design.buildings.some((building) =>
    building.storeys.some(storeyHasProgramActivityRefs),
  );

/**
 * Keep {@link activities} when they already include a program. Otherwise
 * append preset program refs so an empty floor is not Unset. Several programs
 * are skipped once another floor already has a mix; one program is still
 * applied (DEV-3765).
 */
const appendDesignProgramRefs = (
  activities: ActivityReference[],
  design: BuildingDesign,
): ActivityReference[] => {
  if (activities.some(activityRefGuards.is.programActivityRef))
    return activities;
  const programRefs = createProgramActivityRefs(design.activities);
  if (programRefs.length === 0) return activities;
  // An even split of several programs would invent a mix beside a floor that
  // already has one. A single program is the only default worth copying.
  if (programRefs.length !== 1 && designHasProgramActivityRefs(design))
    return activities;
  return [...activities, ...programRefs];
};

/**
 * Give storeys without a program a mix copied from another floor in the same
 * building. When no storey in the design has a program yet, or the design has
 * exactly one program, use preset programs on {@link BuildingDesign.activities}
 * (DEV-3749, DEV-3765).
 */
const seedDesignProgramActivities = (
  design: BuildingDesign,
  buildingId?: string,
): BuildingDesign => {
  const building = getBuildingById(design, buildingId);
  if (!building) return design;
  const programSource = building.storeys.find((storey) =>
    storeyHasProgramActivityRefs(storey),
  );
  let changed = false;
  const storeys = building.storeys.map((storey) => {
    if (!guards.is.array(storey.activities)) return storey;
    if (storeyHasProgramActivityRefs(storey)) return storey;
    const nextActivities = programSource
      ? [
          ...storey.activities,
          ...createActivityReferences(
            programSource.activities.filter(
              activityRefGuards.is.programActivityRef,
            ),
          ),
        ]
      : appendDesignProgramRefs(storey.activities, design);
    if (nextActivities === storey.activities) return storey;
    changed = true;
    return { ...storey, activities: nextActivities };
  });
  if (!changed) return design;
  return setBuildingStoreys(design, building.id, storeys);
};

/**
 * Replace a building's storeys. The first footprint on an empty building
 * receives preset program refs from {@link BuildingDesign.activities} when
 * no storey in the design has a program yet, or when the design has exactly
 * one program (DEV-3749, DEV-3765). Later edits do not put a removed program
 * back.
 */
export const setBuildingStoreysWithProgramSeed = (
  design: BuildingDesign,
  buildingId: string | undefined,
  storeys: Storey[],
): BuildingDesign => {
  const wasEmpty = isEmptyBuildingGeometry(getBuildingById(design, buildingId));
  const next = setBuildingStoreys(design, buildingId, storeys);
  if (!wasEmpty) return next;
  return seedDesignProgramActivities(next, buildingId);
};

/**
 * Ground storey of a sibling that already has activity refs, used to seed a
 * newly drawn or GFA-filled building so programs and calculated-area refs
 * are not dropped.
 */
export const getSiblingActivitySourceStorey = (
  design: BuildingDesign,
  excludeBuildingId?: string,
): Storey | undefined => {
  for (const building of design.buildings) {
    if (building.id === excludeBuildingId) continue;
    const ground = building.storeys[0];
    if (storeyHasActivityRefs(ground)) return ground;
  }
  return undefined;
};

/**
 * Write {@link ground} as storey 0 and copy its footprint onto shapeless
 * floors above so a pre-added stack stays vertically connected.
 * @param storeys - Existing storeys of the building being filled
 * @param ground - Ground storey that already has the new footprint
 */
export const applyGroundFootprintToEmptyStoreys = (
  storeys: Storey[],
  ground: Storey,
): Storey[] => {
  if (!storeys.length) return [ground];
  return storeys.map((storey, index) => {
    if (index === 0) return ground;
    if (
      designGuards.is.mezzanineStorey(storey) ||
      !isEmptyStoreysGeometry([storey])
    ) {
      return storey;
    }
    return {
      ...storey,
      shapes: ground.shapes.map((shape) =>
        createShape({ points: shape.points, cutout: shape.cutout }),
      ),
    };
  });
};

/**
 * Apply footprint {@link shapes} to a ground storey, copying activity refs
 * from {@link source} when the storey has none yet. Existing height is kept.
 */
export const seedGroundStoreyGeometry = (
  existing: Storey | undefined,
  shapes: Shape[],
  source: Storey | undefined,
): Storey => {
  const seedFromSibling = Boolean(source && !storeyHasActivityRefs(existing));
  if (!existing) {
    return createStorey({
      shapes,
      activities: source?.activities,
      height: source?.height,
    });
  }
  if (!seedFromSibling || !source) {
    return { ...existing, shapes };
  }
  return {
    ...existing,
    shapes,
    activities: createActivityReferences(source.activities),
  };
};

/**
 * Empty placeholder to fill with a drawn footprint. Prefers the selected
 * building when it has no geometry; otherwise the first empty sibling so
 * plus-icon close cannot leave a demolished slot behind (DEV-2576).
 */
export const findEmptyBuildingToFill = (
  design: BuildingDesign,
  buildingId?: string,
): Building | undefined => {
  const selected = getBuildingById(design, buildingId);
  if (selected && isEmptyBuildingGeometry(selected)) return selected;
  return design.buildings.find(
    (building) => building !== selected && isEmptyBuildingGeometry(building),
  );
};

/**
 * Map every storey in every {@link Building} of the design (DEV-2576).
 * @param design - Building design
 * @param mapStorey - Maps a single storey to its next value
 */
export const mapStoreysInDesign = (
  design: BuildingDesign,
  mapStorey: (storey: Storey) => Storey,
): BuildingDesign => ({
  ...design,
  buildings: design.buildings.map((building) => ({
    ...building,
    storeys: building.storeys.map(mapStorey),
  })),
});

/**
 * Get the height of the storeys below the ground floor.
 * Will not include mezzanine storeys.
 * @param root Storeys of one building (not a {@link BuildingDesign})
 * @returns Sum of non-mezzanine storey heights
 */
export const getStoreysHeight = (root: Storey[]): number => {
  const storeys = root.filter(designGuards.is.nonMezzanineStorey);
  return sum(storeys.map((s) => s.height));
};

/**
 * Get all storey under a given storey
 * @param root
 * @param storey
 * @returns
 */
export const getStoreysBelow = (
  root: BuildingDesign | Storey[],
  storey: Storey,
): Storey[] => {
  const storeys = getStoreysOwningSelector(root, storey);
  const index = getStoreyIndex(storeys, storey);
  if (index <= 0) return [];
  return storeys.slice(0, index);
};

/**
 * Get elevation of a certain storey.
 * Calculated as the sum of the height of the storeys below it and the elevation of the first storey.
 * @param root
 * @param storey
 * @returns
 */
export const getStoreyElevation = (
  root: BuildingDesign | Storey[],
  storey: Storey,
): number => {
  const storeys = getStoreysOwningSelector(root, storey);

  if (guards.is.nonEmptyArray(storeys)) {
    const baseElevation = storeys[0].elevation ?? 0;
    const currentIndex = getStoreyIndex(storeys, storey);
    if (currentIndex < 0) return baseElevation;

    // Handle mezzanine storeys.
    if (designGuards.is.mezzanineStorey(storey)) {
      const parentIndex = getMezzanineParentIndex(storeys, currentIndex);
      const parentStorey =
        parentIndex === undefined ? undefined : storeys[parentIndex];

      const parentElevation = parentStorey
        ? getStoreyElevation(storeys, parentStorey)
        : baseElevation;

      // Place mezzanine relative to its parent so it visually floats.
      return parentElevation + (storey.mezzanine_elevation ?? 0);
    }

    // Handle non-mezzanine storeys.
    const subStoreys = getStoreysBelow(storeys, storey);
    return getStoreysHeight(subStoreys) + baseElevation;
  }
  return 0;
};

export type ElevationRange = {
  bottom: number;
  top: number;
};

/**
 * Storeys whose tops/bottoms are this close (m) are treated as touching a slab,
 * not as overlapping in height. Shared by envelope cover, cutout atrium checks,
 * and {@link getElevationRangeOverlapHeight}.
 */
export const ELEVATION_TOUCH_EPSILON = 1e-3;

/**
 * Vertical span of {@link storey} (metres, ground = 0). Same bottom as
 * {@link getStoreyElevation}; top is bottom plus storey height.
 */
export const getStoreyElevationRange = (
  root: BuildingDesign | Storey[],
  storey: Storey,
): ElevationRange => {
  const bottom = getStoreyElevation(root, storey);
  return { bottom, top: bottom + storey.height };
};

/**
 * Shared interior height of two elevation bands (m). Touching at a slab only
 * (one top equals the other bottom), or overlapping by at most
 * {@link ELEVATION_TOUCH_EPSILON}, is 0.
 */
export const getElevationRangeOverlapHeight = (
  a: ElevationRange,
  b: ElevationRange,
): number => {
  const overlap = Math.min(a.top, b.top) - Math.max(a.bottom, b.bottom);
  // Float noise around a slab joint is not interior overlap.
  return overlap <= ELEVATION_TOUCH_EPSILON ? 0 : overlap;
};

/**
 * True when two storey bands share interior height. Touching at a slab only
 * (one top equals the other bottom) does not count.
 */
export const doElevationRangesOverlap = (
  a: ElevationRange,
  b: ElevationRange,
): boolean => getElevationRangeOverlapHeight(a, b) > 0;

/**
 * True when two bands either share interior height or are separated by a gap.
 * Slab contact (overlap within {@link ELEVATION_TOUCH_EPSILON}) is false so
 * stacked buildings that only meet at a floor are allowed.
 */
export const doElevationRangesConflict = (
  a: ElevationRange,
  b: ElevationRange,
): boolean => {
  const overlap = Math.min(a.top, b.top) - Math.max(a.bottom, b.bottom);
  return Math.abs(overlap) > ELEVATION_TOUCH_EPSILON;
};

/**
 * Storeys whose walls occupy the same heights as {@link range}.
 */
export const getStoreysOverlappingElevationRange = (
  storeys: Storey[],
  range: ElevationRange,
): Storey[] =>
  storeys.filter((storey) =>
    doElevationRangesOverlap(getStoreyElevationRange(storeys, storey), range),
  );

export type StoreyPointChainEntry = {
  id: Storey['id'];
  index: number;
};

export type StoreyInsertChainEntry = StoreyPointChainEntry & {
  shapeId: Shape['id'];
  insertIndex: number;
};

const getUniquePointCount = (points: Point2D[]): number => {
  if (points.length <= 1) return points.length;
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return points.length;
  return isEqualPoints(first, last) ? points.length - 1 : points.length;
};

const resolveSegmentInsertIndex = (
  points: Point2D[],
  segmentStart: Point2D,
  segmentEnd: Point2D,
): number | undefined => {
  const uniqueLength = getUniquePointCount(points);
  if (!uniqueLength) return undefined;
  for (let index = 0; index < uniqueLength; index += 1) {
    const current = points[index];
    const next = index + 1 < uniqueLength ? points[index + 1] : points[0];
    if (!current || !next) continue;
    const matchesForward =
      isEqualPoints(current, segmentStart) && isEqualPoints(next, segmentEnd);
    const matchesReverse =
      isEqualPoints(current, segmentEnd) && isEqualPoints(next, segmentStart);
    if (!matchesForward && !matchesReverse) continue;
    const insertIndex = index + 1;
    if (insertIndex < uniqueLength) return insertIndex;
    return uniqueLength;
  }
  return undefined;
};

/**
 * Resolve storeys where a matching wall segment exists above the selected storey.
 * Returns insert indexes per storey to keep add-point drags in sync.
 */
export const getStoreyInsertChainAbove = (
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
  shapeId: Shape['id'] | undefined,
  segmentStart: Point2D | undefined,
  segmentEnd: Point2D | undefined,
  baseInsertIndex?: number,
): StoreyInsertChainEntry[] => {
  if (
    !selectedStoreyId ||
    !shapeId ||
    !segmentStart ||
    !segmentEnd ||
    !storeys.length
  ) {
    return [];
  }
  const selectedIndex = storeys.findIndex(
    (storey) => storey.id === selectedStoreyId,
  );
  if (selectedIndex < 0) return [];
  const selectedStorey = storeys[selectedIndex];
  const isMezzanine = designGuards.is.mezzanineStorey(selectedStorey);
  const matches: StoreyInsertChainEntry[] = [];
  const resolveStoreyMatch = (
    storey: Storey,
    preferredShapeId: Shape['id'] | undefined,
  ): { shapeId: Shape['id']; insertIndex: number } | undefined => {
    const shapes = storey.shapes ?? [];
    if (!shapes.length) return undefined;
    const inspectShape = (
      candidate: Shape | undefined,
    ): { shapeId: Shape['id']; insertIndex: number } | undefined => {
      if (!candidate?.points?.length) return undefined;
      const insertIndex = resolveSegmentInsertIndex(
        candidate.points,
        segmentStart,
        segmentEnd,
      );
      if (insertIndex === undefined) return undefined;
      return { shapeId: candidate.id, insertIndex };
    };
    if (preferredShapeId) {
      const preferredShape = shapes.find(
        (candidate) => candidate.id === preferredShapeId,
      );
      const preferredMatch = inspectShape(preferredShape);
      if (preferredMatch) return preferredMatch;
    }
    for (const candidate of shapes) {
      if (candidate?.id === preferredShapeId) continue;
      const match = inspectShape(candidate);
      if (match) return match;
    }
    return undefined;
  };

  type StoreyInsertMatch = { shapeId: Shape['id']; insertIndex: number };
  for (let index = selectedIndex; index < storeys.length; index += 1) {
    const storey = storeys[index];
    if (!storey) break;
    // When adding a point on a mezzanine, do not propagate to regular storeys above.
    if (isMezzanine && !designGuards.is.mezzanineStorey(storey)) continue;
    const match: StoreyInsertMatch | undefined =
      index === selectedIndex && baseInsertIndex !== undefined && shapeId
        ? { shapeId, insertIndex: baseInsertIndex }
        : resolveStoreyMatch(storey, shapeId);

    // Continue to next storey if no match. This will include all storeys matching shape
    if (!match) continue;
    matches.push({
      id: storey.id,
      index,
      insertIndex: match.insertIndex,
      shapeId: match.shapeId,
    });
    shapeId = match.shapeId;
  }
  return matches;
};

/**
 * Get the storey below a given storey exluding mezzanine storeys
 * @param storeys
 * @param itemOrId
 * @returns
 */
export const getStoreyBelow = (
  storeys: Storey[],
  itemOrId: ItemOrItemId<Storey>,
): NonMezzanineStorey | undefined => {
  const id = getId(itemOrId);
  const index = storeys.findIndex((storey) => storey.id === id);
  for (let i = index - 1; i >= 0; i--) {
    if (designGuards.is.nonMezzanineStorey(storeys[i])) {
      return storeys[i];
    }
  }
  return storeys[index - 1];
};

/**
 * Get the next non-mezzanine storey above a given storey.
 * @param storeys Ordered storey array (bottom → top)
 * @param itemOrId Storey or storey id to search from
 * @returns The first {@link NonMezzanineStorey} above, or undefined
 */
export const getStoreyAbove = (
  storeys: Storey[],
  itemOrId: ItemOrItemId<Storey>,
): NonMezzanineStorey | undefined => {
  const id = getId(itemOrId);
  const index = storeys.findIndex((storey) => storey.id === id);
  if (index < 0) return undefined;
  for (let i = index + 1; i < storeys.length; i++) {
    if (designGuards.is.nonMezzanineStorey(storeys[i])) {
      return storeys[i];
    }
  }
  return undefined;
};

/**
 * True when a storey cannot be removed because it carries mezzanines: the
 * ground floor stays while a mezzanine sits above it, since the mezzanine
 * resolves its elevation against that floor.
 * @param storeys - Storeys of the building the storey belongs to
 * @param storey - Storey about to be removed
 */
export const isStoreyRemovalBlockedByMezzanine = (
  storeys: Storey[],
  storey: Storey,
): boolean =>
  storeys[0]?.id === storey.id &&
  getMezzanineStoreysInStorey(storeys, storey).length > 0;

/**
 * Get all mezzanine storeys in a storey.
 * @param root
 * @param storey
 * @returns
 */
export const getMezzanineStoreysInStorey = (
  root: BuildingDesign | Storey[],
  storey: Storey,
): MezzanineStorey[] => {
  if (designGuards.is.mezzanineStorey(storey)) {
    return [];
  }
  const storeys = getStoreysOwningSelector(root, storey);
  const storeyIndex = getStoreyIndex(storeys, storey);
  const mezzanineStoreys: MezzanineStorey[] = [];

  // Add all storeys above the selected storey that are mezzanine (until first non-mezzanine storey).
  for (let index = storeyIndex + 1; index < storeys.length; index += 1) {
    const candidate = storeys[index];
    if (!designGuards.is.mezzanineStorey(candidate)) break; // Stop at first non-mezzanine storey.
    mezzanineStoreys.push(candidate);
  }
  return mezzanineStoreys;
};

/**
 * Find the nearest non-mezzanine storey below a mezzanine.
 */
export const getMezzanineParentIndex = (
  root: BuildingDesign | Storey[],
  indexOrMezzanine: number | Storey,
): number | undefined => {
  const parent = getMezzanineParent(root, indexOrMezzanine);
  return parent ? getStoreyIndex(root, parent) : undefined;
};

/**
 * Get the parent of a mezzanine storey by finding the nearest non-mezzanine below it.
 * @param root
 * @param indexOrMezzanine
 * @returns
 */
export const getMezzanineParent = (
  root: BuildingDesign | Storey[],
  indexOrMezzanine: number | Storey,
): Storey | undefined => {
  const storeys = guards.is.number(indexOrMezzanine)
    ? getStoreys(root)
    : getStoreysOwningSelector(root, indexOrMezzanine);
  const mezzanineStorey = guards.is.number(indexOrMezzanine)
    ? storeys[indexOrMezzanine]
    : indexOrMezzanine;
  if (!mezzanineStorey) return undefined;
  // Make sure provided index or storey is a mezzanine (else there is no parent).
  if (!designGuards.is.mezzanineStorey(mezzanineStorey)) {
    return undefined;
  }
  return getStoreyBelow(storeys, mezzanineStorey);
};

/**
 * Get the storey containing a certain shape
 * @param root
 * @param shapeOrId
 * @returns
 */
export const getStoreyContainingShape = (
  root: BuildingDesign | Storey[],
  shapeOrId: ItemOrItemId<Shape> | undefined,
): Storey | undefined => {
  const storeys = designGuards.is.buildingDesign(root)
    ? getAllStoreys(root)
    : getStoreys(root);
  if (!shapeOrId) return undefined;
  const shapeId = getId(shapeOrId);
  return storeys.find((storey) => storey.shapes.find((s) => s.id === shapeId));
};

/** Max distance (meters) below which adjacent points are merged */
const MERGE_DISTANCE_THRESHOLD = 0.1;

/**
 * Remove near-duplicate adjacent points from all shapes in all storeys.
 * When two consecutive points are within {@link MERGE_DISTANCE_THRESHOLD},
 * the second point is dropped. Preserves shapes with fewer than 3 remaining
 * points unchanged to avoid creating degenerate geometry.
 */
export const mergeNearbyPointsInStoreys = (storeys: Storey[]): Storey[] => {
  let changed = false;

  const result = storeys.map((storey) => {
    let shapesChanged = false;
    const newShapes = storey.shapes.map((shape) => {
      const merged = mergeNearbyShapePoints(shape.points);
      if (merged.length === shape.points.length) return shape;
      if (merged.length < 3) return shape;
      shapesChanged = true;
      return { ...shape, points: merged as Polygon2D };
    });
    if (!shapesChanged) return storey;
    changed = true;
    return { ...storey, shapes: newShapes };
  });

  return changed ? result : storeys;
};

/**
 * Merge adjacent points that are closer than {@link MERGE_DISTANCE_THRESHOLD}.
 * Also checks the wrap-around (last point vs first point).
 */
const mergeNearbyShapePoints = (points: readonly Point2D[]): Point2D[] => {
  if (points.length < 2) return [...points];

  const first = points[0];
  if (!first) return [...points];
  const result: Point2D[] = [first];

  for (let i = 1; i < points.length; i++) {
    const current = points[i];
    const prev = result[result.length - 1];
    if (!current || !prev) continue;
    if (getDistance(prev, current) < MERGE_DISTANCE_THRESHOLD) continue;
    result.push(current);
  }

  // Check wrap-around: last vs first
  if (result.length > 1) {
    const last = result[result.length - 1];
    if (last && getDistance(last, first) < MERGE_DISTANCE_THRESHOLD) {
      result.pop();
    }
  }

  return result;
};
